/**
 * Delivery Alerts Panel — dashboard for the download-reminder queue health.
 *
 * Sits at the top of the Security & Ops tab so admin sees the queue state
 * immediately on landing. Colour-coded status LED, current count, thresholds,
 * and adjacent action buttons (RESCUE NOW + RE-ARM FAILED) so the incident
 * response is one click away from the alert context.
 *
 * Traffic-light convention:
 *   healthy → emerald  (count < warning threshold)
 *   warning → amber    (count ≥ warning, < critical)
 *   critical → rose    (count ≥ critical)
 */
import { AlertOctagon, LifeBuoy, Loader2, Mail, RefreshCw, ShieldAlert, ShieldCheck } from "lucide-react";

const STATUS_STYLE = {
  healthy: {
    dot: "bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.15)]",
    label: "HEALTHY",
    labelColor: "text-emerald-700",
    strip: "border-emerald-200",
    icon: ShieldCheck,
  },
  warning: {
    dot: "bg-amber-500 shadow-[0_0_0_4px_rgba(245,158,11,0.15)]",
    label: "WARNING",
    labelColor: "text-amber-700",
    strip: "border-amber-300",
    icon: ShieldAlert,
  },
  critical: {
    dot: "bg-rose-600 shadow-[0_0_0_4px_rgba(225,29,72,0.18)]",
    label: "CRITICAL",
    labelColor: "text-rose-700",
    strip: "border-rose-300",
    icon: AlertOctagon,
  },
};

const fmtIso = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const fmtMinutes = (m) => {
  if (m == null) return "—";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
};

export const DeliveryAlertsPanel = ({
  health,
  busy,
  onReload,
  onRescueNow,
  onReArmFailed,
}) => {
  const status = health?.status || "healthy";
  const style = STATUS_STYLE[status] || STATUS_STYLE.healthy;
  const StatusIcon = style.icon;
  const total = health?.rescue_pending?.total ?? 0;
  const oldest = health?.rescue_pending?.oldest_minutes;
  const alerts = health?.alerts_active || [];
  const thresholds = health?.thresholds || { warning: 3, critical: 5, alert_after_minutes: 60 };

  return (
    <div
      data-testid="delivery-alerts-panel"
      className={`bg-[#0A192F] text-white border-t-2 ${style.strip} shadow-lg`}
    >
      {/* Header strip */}
      <div className="px-6 py-4 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-3">
          <StatusIcon size={18} className="text-[#D4AF37]" />
          <span className="font-accent text-xs tracking-[0.2em] text-white/90">
            DELIVERY ALERTS
          </span>
          <span className={`inline-flex items-center gap-2 pl-3 ml-2 border-l border-white/20 ${style.labelColor}`}>
            <span
              data-testid="delivery-status-led"
              className={`h-2.5 w-2.5 rounded-full ${style.dot}`}
            />
            <span data-testid="delivery-status-label" className="font-accent text-[11px] tracking-widest">
              {style.label}
            </span>
          </span>
        </div>
        <button
          data-testid="delivery-alerts-reload"
          onClick={onReload}
          disabled={busy === "delivery-health"}
          className="inline-flex items-center gap-1.5 text-[11px] text-white/70 hover:text-white disabled:opacity-50"
        >
          {busy === "delivery-health" ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <RefreshCw size={12} />
          )}
          Refresh
        </button>
      </div>

      {/* Body grid: metrics + action column */}
      <div className="grid lg:grid-cols-3 gap-6 p-6">
        {/* Big count card */}
        <div className="lg:col-span-1 flex flex-col justify-between border border-[#D4AF37]/30 bg-white/[0.03] p-5">
          <div>
            <div className="font-accent text-[10px] tracking-widest text-white/50">
              RESCUE PENDING
            </div>
            <div
              data-testid="delivery-pending-count"
              className="text-5xl font-display font-semibold text-[#D4AF37] leading-tight mt-1"
            >
              {total}
            </div>
            <div className="text-[11px] text-white/60 mt-1">
              deliveries stuck or unattempted
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/10 text-[11px] text-white/70 space-y-1">
            <div className="flex justify-between">
              <span>Oldest in queue</span>
              <span data-testid="delivery-oldest-minutes" className="text-white/90">{fmtMinutes(oldest)}</span>
            </div>
            <div className="flex justify-between">
              <span>Warning ≥</span>
              <span className="text-amber-300">{thresholds.warning}</span>
            </div>
            <div className="flex justify-between">
              <span>Critical ≥</span>
              <span className="text-rose-300">{thresholds.critical}</span>
            </div>
            <div className="flex justify-between">
              <span>Fire alert after</span>
              <span className="text-white/90">{thresholds.alert_after_minutes} min sustained</span>
            </div>
          </div>
        </div>

        {/* Active alerts + history */}
        <div className="lg:col-span-1 border border-white/10 bg-white/[0.03] p-5 flex flex-col">
          <div className="font-accent text-[10px] tracking-widest text-white/50 mb-3">
            ACTIVE ALERTS
          </div>
          {alerts.length === 0 ? (
            <div
              data-testid="delivery-no-alerts"
              className="flex-1 flex items-center justify-center text-white/40 text-xs"
            >
              No active alerts.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="delivery-alert-list">
              {alerts.map((a, i) => (
                <li
                  key={i}
                  data-testid={`delivery-alert-item-${i}`}
                  className="text-[11px] border-l-2 border-rose-500 pl-3 py-1"
                >
                  <div className="text-white/90">{a.message}</div>
                  <div className="text-white/50 mt-0.5">since {fmtIso(a.since)}</div>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 pt-4 border-t border-white/10 text-[11px] text-white/70 space-y-1.5">
            <div className="flex justify-between">
              <span>Last alert fired</span>
              <span data-testid="delivery-last-alert" className="text-white/90">
                {fmtIso(health?.last_alert_fired)}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Last resolved</span>
              <span data-testid="delivery-last-resolved" className="text-white/90">
                {fmtIso(health?.last_resolved_at)}
              </span>
            </div>
          </div>
        </div>

        {/* Action column — rescue buttons live here for one-click response */}
        <div className="lg:col-span-1 flex flex-col gap-3">
          <button
            data-testid="delivery-rescue-now-btn"
            onClick={onRescueNow}
            disabled={busy === "rescue" || total === 0}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#D4AF37] text-[#0A192F] font-accent text-[11px] tracking-widest hover:bg-[#E5C158] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy === "rescue" ? (
              <><Loader2 size={12} className="animate-spin" /> RESCUING…</>
            ) : (
              <><LifeBuoy size={14} /> RESCUE NOW ({total})</>
            )}
          </button>
          <button
            data-testid="delivery-rearm-btn"
            onClick={onReArmFailed}
            disabled={busy === "rearm"}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-white/25 text-white font-accent text-[11px] tracking-widest hover:bg-white/10 disabled:opacity-40"
          >
            {busy === "rearm" ? (
              <><Loader2 size={12} className="animate-spin" /> WORKING…</>
            ) : (
              <><Mail size={14} /> RE-ARM FAILED</>
            )}
          </button>
          <div className="text-[10px] text-white/50 leading-relaxed mt-1">
            <strong className="text-white/70">RESCUE NOW</strong> sends the download-reminder to every pending customer, bypassing the scheduler&rsquo;s 30-day window and max-attempts cap (5s throttle). <strong className="text-white/70">RE-ARM FAILED</strong> resets state on previously-attempted-but-not-delivered records so the scheduler retries them.
          </div>
        </div>
      </div>
    </div>
  );
};
